import React, { useState } from 'react';
import api from '../../utils/axios';
import { Input } from '../ui/input';
import { Users } from 'lucide-react';
import { CvuToolPreview } from './CvuToolPreview';

const cliente = (f) => (
  <div><p className="font-medium text-slate-800">{f.razon_social || '—'}</p><p className="text-slate-400 font-mono">{f.cuit}</p></div>
);
const mono = (v) => <span className="font-mono">{v || '—'}</span>;

const TOTALES = [
  { key: 'filas', label: 'Filas' },
  { key: 'cvus_leidos', label: 'CVUs leídos' },
  { key: 'cuits', label: 'CUITs' },
  { key: 'clientes_existentes', label: 'Ya en el padrón' },
  { key: 'clientes_nuevos', label: 'Clientes nuevos', tono: 'blue' },
  { key: 'clientes_nuevos_eliminados_en_waya', label: 'Nuevos eliminados en Waya', tono: 'amber' },
  { key: 'eliminados_en_waya', label: 'Del padrón, eliminados en Waya', tono: 'amber' },
  { key: 'estado_waya_a_guardar', label: 'Estado en Waya a guardar', tono: 'blue' },
  { key: 'pep_agregados', label: 'PEP que se agregan', tono: 'rose' },
  { key: 'pep_diferencias', label: 'PEP distinto (se mantiene)', tono: 'amber' },
  { key: 'cvus_agregados', label: 'CVUs que se agregan', tono: 'blue' },
  { key: 'cvus_agregados_banco_anterior', label: 'De ellos, banco anterior', tono: 'amber' },
  { key: 'cvus_metadatos', label: 'CVUs con datos del reporte' },
  { key: 'cvus_en_otro_cliente', label: 'CVU de otro cliente', tono: 'rose' },
  { key: 'cvus_estado_distinto', label: 'Estado distinto (no se toca)', tono: 'amber' },
  { key: 'errores', label: 'Filas con error', tono: 'rose' },
  { key: 'aplicados', label: 'Aplicados', tono: 'emerald' },
];

const TABLAS = [
  { key: 'clientes_nuevos', titulo: 'Clientes que no están en el padrón (se crean con origen reporte_usuarios_waya)', columnas: [
    { key: 'cliente', label: 'Cliente', render: cliente }, { key: 'tipo', label: 'Tipo' }, { key: 'pep', label: 'PEP' },
    { key: 'estado_waya', label: 'Estado en Waya' }, { key: 'cvus', label: 'CVUs', render: (f) => mono(f.cvus) },
  ] },
  { key: 'eliminados_en_waya', titulo: 'Clientes del padrón que figuran eliminados en Waya (solo se guarda el estado en Waya)', columnas: [
    { key: 'cliente', label: 'Cliente', render: cliente }, { key: 'estado_waya', label: 'Estado en Waya' },
  ] },
  { key: 'pep_agregados', titulo: 'Se marcan como PEP', columnas: [{ key: 'cliente', label: 'Cliente', render: cliente }] },
  { key: 'pep_diferencias', titulo: 'Diferencias de PEP (el reporte no saca la marca)', columnas: [
    { key: 'cliente', label: 'Cliente', render: cliente }, { key: 'detalle', label: 'Detalle' },
  ] },
  { key: 'cvus_agregados', titulo: 'CVUs que se agregan a clientes existentes', columnas: [
    { key: 'cliente', label: 'Cliente', render: cliente }, { key: 'cvu', label: 'CVU', render: (f) => mono(f.cvu) },
    { key: 'banco', label: 'Banco sponsor' }, { key: 'estado', label: 'Estado' },
  ] },
  { key: 'cvus_en_otro_cliente', titulo: 'CVUs que ya son de otro cliente (no se tocan)', columnas: [
    { key: 'cliente', label: 'Cliente del reporte', render: cliente }, { key: 'cvu', label: 'CVU', render: (f) => mono(f.cvu) },
    { key: 'cuit_dueno', label: 'CUIT en el padrón', render: (f) => mono(f.cuit_dueno) },
  ] },
  { key: 'cvus_estado_distinto', titulo: 'Estado distinto al del padrón (no se modifica)', columnas: [
    { key: 'cliente', label: 'Cliente', render: cliente }, { key: 'cvu', label: 'CVU', render: (f) => mono(f.cvu) },
    { key: 'estado_sistema', label: 'En el padrón' }, { key: 'estado_reporte', label: 'En el reporte' },
  ] },
  { key: 'errores', titulo: 'Filas con error', columnas: [
    { key: 'fila', label: 'Fila' }, { key: 'cuit', label: 'CUIT' }, { key: 'detalle', label: 'Detalle' },
  ] },
];

export const ReporteUsuariosTool = () => {
  const [archivo, setArchivo] = useState(null);
  const run = async (ejecutar) => {
    const fd = new FormData();
    fd.append('file', archivo);
    const res = await api.post(`/padron-interno/importar-reporte-usuarios?ejecutar=${ejecutar}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000,
    });
    return res.data;
  };
  return (
    <CvuToolPreview
      testid="tool-reporte-usuarios" icono={Users} titulo="Importar Reporte de usuarios (Waya)"
      descripcion="Excel 'Reporte de usuarios - Padrón' de Waya (una fila por billetera, con CVUs vigentes e historial). Crea los CUITs que faltan, agrega CVUs, guarda alta/baja PSP y la marca PEP (solo la agrega, nunca la saca). Antes de aplicar se hace un backup del padrón."
      run={run} disabled={!archivo} resetKey={archivo} totalesDefs={TOTALES} tablas={TABLAS}
      puedeAplicar={(d) => ['clientes_nuevos', 'pep_agregados', 'cvus_agregados', 'cvus_metadatos', 'estado_waya_a_guardar'].some((k) => (d.totales?.[k] || 0) > 0)}
      textoConfirmacion="Se hará un backup del padrón y luego se crearán los clientes nuevos, se agregarán los CVUs y las marcas PEP listadas, se guardará el estado en Waya de cada cliente y los datos del reporte en cada CVU."
    >
      <Input type="file" accept=".xlsx,.xls" className="max-w-sm h-9 text-sm"
        onChange={(e) => setArchivo(e.target.files?.[0] || null)} data-testid="tool-reporte-usuarios-file" />
    </CvuToolPreview>
  );
};
